import { FC } from 'react';
import { ButtonBase, Typography, styled } from '@mui/material';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { PolarisIcon } from '@/exports/columnCommonComponents';

export type IncrementalLaunchStepState = 'inactive' | 'active';

export type IncrementalLaunchStepProps = {
  icon?: string;
  isCompleted?: boolean;
  label: string;
  onClick?: () => void;
  state: IncrementalLaunchStepState;
  step: number;
};

const STEP_BOX_SIZE = 24;

const IncrementalLaunchStep: FC<IncrementalLaunchStepProps> = ({ icon, isCompleted = false, label, onClick, state, step }) => {
  const isClickable = Boolean(onClick);

  return (
    <StepContainer $clickable={isClickable} $state={state} disableRipple={!isClickable} onClick={onClick}>
      <StepPill>
        <StepNumber>{step}</StepNumber>
        <StepLabel>
          <Typography sx={{ color: 'common.white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{label}</Typography>
        </StepLabel>
        {icon && (
          <StepResponsiveIcon>
            <PolarisIcon icon={icon} style={{ fontSize: 18 }} />
          </StepResponsiveIcon>
        )}
      </StepPill>
      <StepCompletedIcon>{isCompleted && <PolarisIcon icon="complete_outline" style={{ fontSize: 22 }} />}</StepCompletedIcon>
    </StepContainer>
  );
};

export default IncrementalLaunchStep;

const StepContainer = styled(ButtonBase, {
  shouldForwardProp: (prop: string) => prop !== '$clickable' && prop !== '$state'
})<{
  $clickable: boolean;
  $state: IncrementalLaunchStepState;
}>`
  align-items: center;
  background-color: ${({ theme }) => (theme as FocusTheme).palette.background.paper};
  border-radius: 8px 0 0 8px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  display: flex;
  justify-content: flex-start;
  opacity: ${({ $state }) => ($state === 'inactive' ? 0.45 : 1)};
  padding: ${({ theme }) => theme.spacing(2)};
  gap: ${({ theme }) => theme.spacing(3)};
  text-align: left;
  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;
  width: 100%;

  ${({ $clickable, theme }) =>
    $clickable &&
    `
      &:hover {
        background-color: ${(theme as FocusTheme).palette.neutral[80]};
      }
    `}
`;

const StepPill = styled('div')(({ theme }) => ({
  alignItems: 'stretch',
  border: `1px solid ${(theme as FocusTheme).palette.common.white}`,
  borderRadius: theme.shape.borderRadius,
  display: 'inline-flex',
  flex: '0 1 auto',
  maxWidth: '100%',
  minWidth: 0,
  [theme.breakpoints.up('xl')]: {
    minWidth: 155
  }
}));

const StepNumber = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: (theme as FocusTheme).palette.common.white,
  borderRadius: '3px 0 0 3px',
  color: (theme as FocusTheme).palette.background.paper,
  display: 'flex',
  flex: '0 0 auto',
  fontWeight: theme.typography.fontWeightBold,
  height: STEP_BOX_SIZE,
  justifyContent: 'center',
  lineHeight: 1,
  minWidth: STEP_BOX_SIZE
}));

const StepLabel = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  height: STEP_BOX_SIZE,
  padding: theme.spacing(0, 1.5),
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('xl')]: {
    display: 'none'
  }
}));

const StepResponsiveIcon = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'none',
  flex: '0 0 auto',
  height: STEP_BOX_SIZE,
  justifyContent: 'center',
  minWidth: STEP_BOX_SIZE,
  [theme.breakpoints.down('xl')]: {
    display: 'flex'
  }
}));

const StepCompletedIcon = styled('div')(({ theme }) => ({
  alignItems: 'center',
  color: (theme as FocusTheme).palette.success.main,
  height: STEP_BOX_SIZE,
  minWidth: STEP_BOX_SIZE
}));
